import jsPDF from "jspdf";
import { partyScreeningRows, PARTY_STATUS_LABEL } from "./shared/party-screening.js";
import { financialRangeRows } from "./shared/financial-ranges.js";
import { reconcileCaseState } from "./shared/case-state.js";
import { caseFeatures } from "./shared/case-features.js";
import { caseOwnershipThreshold, holdingPercent } from "./shared/ownership-threshold.js";
import { reviewPeriodShown } from "./shared/review-period.js";
import { cleanModelText } from "./shared/case-note-text.js";
import { isIndividualCase } from "./shared/individual-case.js";
import { individualFormALine } from "./shared/individual-record.js";
import { tierInForce, ASSESSMENT_PENDING } from './shared/tier-in-force.js';

// formA (Form A in the exports, 22/10/2026): for an individual case, { formRequests, individualFormA }
// as the board passes them (formRequests only when read without error).
export function generateJaybeeReport(session, narrative, documents, verifications, walletScreenings = [], sowSession = null, firmName = "", features = null, formA = null) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const W = 210, H = 297;
  const ml = 20, mr = 20, mt = 20;
  const contentW = W - ml - mr;
  let y = mt;
  // v1_974: the report's own firm wording (the FinSA paragraph) names the firm,
  // else "the firm"; firmBrand keeps its fallback for the footer and file name.
  const firmBrand = (firmName || "CompliantID").trim() || "CompliantID";
  const firmFilePrefix = (firmBrand.replace(/[^\w]+/g, "_").replace(/^_+|_+$/g, "")) || "Firm";

  const colors = {
    black: [0, 0, 0],
    darkGrey: [60, 60, 60],
    midGrey: [120, 120, 120],
    lightGrey: [200, 200, 200],
    veryLight: [245, 245, 245],
    white: [255, 255, 255],
    accent: [40, 40, 80],
  };

  const checkPage = (needed = 10) => {
    if (y + needed > H - 20) { doc.addPage(); y = mt; }
  };

  const setFont = (size, style = "normal", color = colors.black) => {
    doc.setFontSize(size);
    doc.setFont("helvetica", style);
    doc.setTextColor(...color);
  };

  const addText = (text, x, fontSize = 10, style = "normal", color = colors.black, maxWidth = contentW) => {
    setFont(fontSize, style, color);
    const lines = doc.splitTextToSize(String(text || "—"), maxWidth);
    checkPage(lines.length * (fontSize * 0.4 + 1));
    doc.text(lines, x, y);
    y += lines.length * (fontSize * 0.4 + 1) + 1;
    return lines.length;
  };

  const addParagraph = (text, label = null) => {
    if (label) {
      checkPage(8);
      setFont(9, "bold", colors.darkGrey);
      doc.text(label, ml, y);
      y += 5;
    }
    setFont(10, "normal", colors.black);
    const lines = doc.splitTextToSize(String(text || "—"), contentW);
    checkPage(lines.length * 5 + 4);
    doc.text(lines, ml, y);
    y += lines.length * 5 + 4;
  };

  let _secNum = 0;
  const addSection = (title) => {
    _secNum += 1;
    checkPage(14);
    y += 4;
    // Section rule
    doc.setDrawColor(...colors.accent);
    doc.setLineWidth(0.5);
    doc.line(ml, y, W - mr, y);
    y += 5;
    setFont(11, "bold", colors.accent);
    doc.text(`${_secNum}. ${title}`, ml, y);
    y += 6;
  };

  const addKeyValue = (label, value, indent = ml) => {
    const val = value && String(value).trim() && String(value) !== "[object Object]" ? String(value) : null;
    if (!val) return; // Skip empty fields
    checkPage(6);
    setFont(9, "bold", colors.darkGrey);
    doc.text(label + ":", indent, y);
    setFont(9, "normal", colors.black);
    const valLines = doc.splitTextToSize(val, contentW - 50);
    doc.text(valLines, indent + 50, y);
    y += Math.max(valLines.length * 4.5, 5);
  };

  const report = session.report || {};
  // v1_747: reconciled case state, so the exported report agrees with the board.
  const cs = reconcileCaseState({
    report,
    collected_data: session.collected_data,
    origin: session.origin,
    documents,
    documentTracking: [],
    sumsubVerifications: Object.fromEntries((verifications || []).filter(v => v && v.person_name).map(v => [v.person_name, v])),
    aiReview: cleanModelText(session.ai_review),
    walletScreenings,
    mlroDecision: session.mlro_decision,
    decisionRecord: session.collected_data && session.collected_data.decisionRecord,
    ...(formA && typeof formA === 'object' ? { formRequests: formA.formRequests, individualFormA: formA.individualFormA } : {}),
  // v1_827: the firm's live flags (the dashboard's), not the portal's saved copy.
  }, features ? { features: caseFeatures(features) } : {});
  const ref = narrative.reference || `CID-${new Date().getFullYear()}-001`;
  const today = new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });

  // ── HEADER ──────────────────────────────────────────────
  doc.setFillColor(...colors.accent);
  doc.rect(0, 0, W, 28, "F");

  doc.setTextColor(...colors.white);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("SAGE CAPITAL MANAGEMENT", ml, 11);
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("CLIENT ACCEPTANCE REPORT — CONFIDENTIAL", ml, 18);
  doc.setFontSize(9);
  doc.text(`Ref: ${ref}`, W - mr - doc.getTextWidth(`Ref: ${ref}`), 11);
  doc.text(`Date: ${today}`, W - mr - doc.getTextWidth(`Date: ${today}`), 18);
  y = 36;

  // ── HEADER META ─────────────────────────────────────────
  doc.setFillColor(...colors.veryLight);
  doc.setDrawColor(...colors.lightGrey);
  doc.rect(ml, y, contentW, 26, "FD");
  y += 5;
  setFont(9, "normal", colors.darkGrey);
  const metaLeft = ml + 4;
  addKeyValue("Prepared by", `Compliance Manager, Compliance Manager — ${firmBrand}`, metaLeft);
  addKeyValue("Classification", "Confidential — Client Due Diligence Report", metaLeft);
  const cdMeta = report.companyDetails || {};
  addKeyValue("Client", (session.company_name || session.contact_name) + (cdMeta.jurisdiction || report.jurisdiction ? ' · ' + (cdMeta.jurisdiction || report.jurisdiction) : ''), metaLeft);
  y += 4;

  // ── RECOMMENDATION BANNER ───────────────────────────────
  checkPage(16);
  const decision = session.mlro_decision || "Pending";
  const bannerColor = decision === "Approve" ? [34, 139, 34] : decision === "Decline" ? [180, 0, 0] : [180, 120, 0];
  doc.setFillColor(...bannerColor);
  doc.rect(ml, y, contentW, 12, "F");
  setFont(12, "bold", colors.white);
  const bannerText = decision === "Approve" ? "RECOMMENDATION: APPROVE" : decision === "Decline" ? "RECOMMENDATION: DECLINE" : "RECOMMENDATION: PENDING REVIEW";
  doc.text(bannerText, W / 2 - doc.getTextWidth(bannerText) / 2, y + 8);
  y += 16;

  // ── 1. EXECUTIVE SUMMARY ────────────────────────────────
  addSection("Executive Summary");
  addParagraph(narrative.executiveSummary);

  // ── AML COMPLIANCE SUMMARY TABLE ────────────────────────
  checkPage(80);
  y += 4;
  setFont(10, "bold", colors.accent);
  doc.text("AML Compliance Summary", ml, y);
  y += 6;

  const sumsubOk = verifications.some(v => v.status === "approved");
  const allDocsOk = documents.length === 0 || documents.every(d => d.assessment !== "Unsatisfactory");
  const hasWallets = walletScreenings.length > 0 || (report.travelRule?.wallets || []).length > 0;
  const tr = report.travelRule;
  const trOk = !hasWallets || (tr && tr.overallStatus === "compliant");
  // v1_830: at or above the case's ownership threshold (25 when the firm sets none).
  const ownT = caseOwnershipThreshold(report);
  const qualifies = (u) => (holdingPercent(u && u.ownership) ?? 0) >= ownT;
  const hasQualifyingUbos = (report.ubos || []).some(qualifies);
  // v1_747: an officer acceptance of source of wealth/funds satisfies this.
  const sowOk = cs.sow.accepted || !hasQualifyingUbos || (report.ubos || []).filter(qualifies).every(u => u.sowAssessment || u.sowSignedOff);

  const checks = [
    ["Contracting party identified", sumsubOk, sumsubOk ? "Sumsub verified" : "Verification pending"],
    ["Beneficial ownership established", (report.ubos || []).length > 0, (report.ubos || []).length > 0 ? `${report.ubos.length} UBO(s) documented` : "No UBOs recorded"],
    ["Tax compliance declaration", !!(report.taxCompliance?.declared), report.taxCompliance?.declared ? "Declaration obtained from client" : report.taxCompliance?.riskIndicatorsPresent ? "Risk indicators present" : "Not collected"],
    ["Risk classification", !!tierInForce(report), tierInForce(report) ? `${tierInForce(report)} risk, rationale documented` : ASSESSMENT_PENDING],
    ["PEP / sanctions screening", sumsubOk, sumsubOk ? "Clear — Sumsub" : "Pending"],
    ["FATF jurisdiction assessment", !(report.fatfFlags?.entityJurisdictionFlagged), !report.fatfFlags ? "Pre-dates FATF check — manually verified" : report.fatfFlags.entityJurisdictionFlagged ? `${report.fatfFlags.fatfStatus || "Flagged"} — EDD applied` : "Clean — no flags"],
    ["Source of funds", !!(cs.sof.accepted || report.sofAssessment?.narrative || report.sofAssessment?.fundingRoute), cs.sof.accepted ? cs.sof.displayScore : (report.sofAssessment?.fundingRoute || report.sofAssessment?.narrative ? "Documented" : "Not documented")],
    [`Source of wealth (UBOs at or above ${ownT}%)`, sowOk, !hasQualifyingUbos ? "No qualifying UBOs" : sowOk ? "All UBOs interviewed & assessed" : "Some UBOs pending"],
    ["Chainalysis wallet screening", !hasWallets || walletScreenings.length > 0, !hasWallets ? "No wallets provided" : `${walletScreenings.length} wallet(s) screened`],
    ["Travel Rule (FATF/Virtual Assets)", trOk, !hasWallets ? "No crypto wallets — not applicable" : trOk ? "All wallets verified" : "Wallets pending verification"],
    ["Documents on file", allDocsOk, documents.length > 0 ? `${documents.length} document(s) — ${allDocsOk ? "all satisfactory" : "review required"}` : "No documents uploaded"],
    ["MLRO internal sign-off", !!(session.mlro_decision), session.mlro_decision ? `Approved by Compliance Manager` : "Pending"],
  ];

  // Table header
  const colW = [contentW * 0.5, contentW * 0.1, contentW * 0.4];
  const colX = [ml, ml + colW[0], ml + colW[0] + colW[1]];
  const rowH = 7;

  doc.setFillColor(...colors.accent);
  doc.rect(ml, y, contentW, rowH, "F");
  setFont(8, "bold", colors.white);
  doc.text("AML Requirement", colX[0] + 2, y + 5);
  doc.text("Status", colX[1] + 2, y + 5);
  doc.text("Evidence / Notes", colX[2] + 2, y + 5);
  y += rowH;

  checks.forEach(([label, ok, note], idx) => {
    checkPage(rowH + 2);
    // Alternating row background
    if (idx % 2 === 0) {
      doc.setFillColor(248, 249, 250);
      doc.rect(ml, y, contentW, rowH, "F");
    }
    // Cell borders
    doc.setDrawColor(...colors.lightGrey);
    doc.setLineWidth(0.1);
    doc.rect(ml, y, contentW, rowH, "S");

    setFont(8, "normal", colors.black);
    doc.text(doc.splitTextToSize(label, colW[0] - 4)[0], colX[0] + 2, y + 5);

    // Tick or cross
    if (ok) {
      doc.setFillColor(34, 139, 34);
      doc.roundedRect(colX[1] + 3, y + 1.5, 8, 5, 1, 1, "F");
      setFont(7, "bold", [255,255,255]);
      doc.text("✓", colX[1] + 5.5, y + 5.2);
    } else {
      doc.setFillColor(180, 0, 0);
      doc.roundedRect(colX[1] + 3, y + 1.5, 8, 5, 1, 1, "F");
      setFont(7, "bold", [255,255,255]);
      doc.text("✗", colX[1] + 5.5, y + 5.2);
    }

    setFont(8, "normal", colors.darkGrey);
    doc.text(doc.splitTextToSize(String(note || "—"), colW[2] - 4)[0], colX[2] + 2, y + 5);
    y += rowH;
  });
  y += 6;

  // ── 2. CLIENT PROFILE ───────────────────────────────────
  addSection("Client Profile");
  addParagraph(narrative.clientProfile);
  y += 2;
  const cd = report.companyDetails || {};
  addKeyValue("Legal Name", cd.legalName || report.legalName || session.company_name);
  addKeyValue("Incorporated", cd.dateOfIncorporation);
  addKeyValue("Jurisdiction", cd.jurisdiction || report.jurisdiction);
  addKeyValue("Registration No.", cd.registrationNumber || report.registrationNumber);
  addKeyValue("Registered Address", cd.registeredAddress || report.registeredAddress);
  addKeyValue("Business Description", cd.businessDescription || report.businessDescription);
  if (report.objectAndPurpose?.object || report.objectAndPurpose?.purpose) {
    y += 2;
    addKeyValue("Object of Relationship", report.objectAndPurpose.object || "—");
    addKeyValue("Purpose of Relationship", report.objectAndPurpose.purpose || "—");
  }

  // ── 3. BENEFICIAL OWNERSHIP ─────────────────────────────
  // ── REGULATORY & LICENSING STATUS ───────────────────────
  // ── CUSTOMER BASE, GEOGRAPHIES & ANTICIPATED ACTIVITY ───
  addSection("Customer Base, Geographies & Anticipated Activity");
  {
    const ap = report.activityProfile || {};
    const frRows = financialRangeRows(session.collected_data?.financialRanges); // client-indicated bands (v1_646)
    const hasAp = ap.customerTypes || ap.sectors || ap.geographiesServed || ap.annualTurnover || ap.monthlyTransactionValue || ap.monthlyTransactionVolume || frRows.length;
    if (hasAp) {
      addKeyValue("Customer types", ap.customerTypes);
      addKeyValue("Sectors served", ap.sectors);
      addKeyValue("Geographies served", ap.geographiesServed);
      addKeyValue("Higher-risk jurisdictions", ap.highRiskJurisdictions);
      addKeyValue("Expected annual turnover", ap.annualTurnover);
      addKeyValue("Expected monthly volume (count)", ap.monthlyTransactionVolume);
      addKeyValue("Expected monthly value", ap.monthlyTransactionValue);
      addKeyValue("Typical transaction size", ap.typicalTransactionSize);
      addKeyValue("Largest transaction size", ap.largestTransactionSize);
      addKeyValue("Main currencies", ap.currencies);
      addKeyValue("Inbound counterparty countries", ap.counterpartyCountriesInbound);
      addKeyValue("Outbound counterparty countries", ap.counterpartyCountriesOutbound);
      // Client-indicated financial ranges — labelled so they never read as verified.
      frRows.forEach(r => addKeyValue(r.label, r.value));
      // v1_747: reconciled activity assessment (officer resolution first, model beneath).
      addKeyValue("Assessment", cs.activity.displayScore + (cs.activity.resolved && cs.activity.modelScore ? ` (the analysis assessed this ${String(cs.activity.modelScore).split(/[—–-]/)[0].trim()})` : ""));
      if (ap.notes) addParagraph(ap.notes, "Notes");
    } else {
      addParagraph("No customer base or anticipated activity profile recorded.");
    }
  }

  addSection("Regulatory & Licensing Status");
  {
    const reg = report.regulatoryStatus || {};
    const amlSup = report.amlSupervision || {};
    const hasReg = reg.regulated !== undefined || reg.regulator || reg.licenceNumber || amlSup.supervised !== undefined || amlSup.sroName;
    if (hasReg) {
      addKeyValue("Regulated", reg.regulated === true ? "Yes" : reg.regulated === false ? "No" : null);
      addKeyValue("Regulator / authority", reg.regulator);
      addKeyValue("Licence type", reg.licenceType);
      addKeyValue("Licence number", reg.licenceNumber);
      addKeyValue("Licence status", reg.licenceStatus);
      addKeyValue("AML / CFT supervised", amlSup.supervised === true ? "Yes" : amlSup.supervised === false ? "No" : null);
      addKeyValue("AML supervisor / SRO", amlSup.sroName || amlSup.regulator);
    } else {
      addParagraph("No regulatory authorisation recorded; the client is treated as an unregulated entity for risk purposes.");
    }
  }

  addSection("Beneficial Ownership & Identity Verification");
  addParagraph(narrative.beneficialOwnership);
  if ((report.ubos || []).length > 0) {
    y += 2;
    (report.ubos || []).forEach((ubo, i) => {
      checkPage(20);
      doc.setFillColor(...colors.veryLight);
      doc.setDrawColor(...colors.lightGrey);
      doc.rect(ml, y, contentW, 18, "FD");
      y += 4;
      const indent = ml + 4;
      addKeyValue("Name", ubo.name, indent);
      addKeyValue("Ownership", `${ubo.ownership}${ubo.indirect ? " indirect" : ""} · ${ubo.nationality} · ${parseFloat(ubo.ownership) < ownT ? (ownT < 25 ? `Shareholder (below ${ownT}%, not a beneficial owner under the firm's threshold)` : "Shareholder (below 25%, not a beneficial owner under Art. 2a para. 3 AMLA)") : `Beneficial owner (at or above ${ownT}%)`}`, indent);
      // v1_742: a UBO who holds through intermediaries shows the path, so the
      // corporate holder is recorded as the intermediate it is, not a UBO.
      if (ubo.indirect && ubo.ownershipPath) addKeyValue("Ownership path", `${ubo.ownershipPath}${(ubo.intermediaries || []).length ? " (through " + ubo.intermediaries.join(", ") + ")" : ""}`, indent);
      const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(ubo.name?.split(" ")[0]?.toLowerCase()));
      addKeyValue("ID Verification", verifMatch ? `${verifMatch.status} (Sumsub ref: ${verifMatch.applicant_id || "—"})` : "Pending", indent);
      if (ubo.sowAssessment) addKeyValue("SOW Assessment", ubo.sowAssessment, indent);
      y += 3;
    });
  }
  if ((report.directors || []).length > 0) {
    y += 2;
    setFont(9, "bold", colors.darkGrey);
    doc.text("Directors / Authorised Persons:", ml, y);
    y += 4;
    (report.directors || []).forEach(d => {
      checkPage(6);
      const name = typeof d === "object" ? d.name : d;
      const role = typeof d === "object" ? d.role : "Director";
      const verifMatch = verifications.find(v => v.person_name?.toLowerCase().includes(name?.split(" ")[0]?.toLowerCase()));
      addKeyValue(role, `${name} · ${verifMatch ? `Verified (${verifMatch.applicant_id || "—"})` : "Pending verification"}`, ml + 4);
    });
  }

  // ── 4. RISK ASSESSMENT ──────────────────────────────────
  addSection("Risk Assessment");
  addParagraph(narrative.riskAssessment);
  y += 2;
  addKeyValue("Risk Tier", tierInForce(report) || ASSESSMENT_PENDING);
  // FinSA can be object or string
  const fc = report.finsaClassification;
  const finsaCategory = fc && typeof fc === 'object' ? (fc.category || fc.label || JSON.stringify(fc)) : String(fc || '—');
  const finsaBasis = fc && typeof fc === 'object' ? fc.basis : null;
  const finsaAssessment = fc && typeof fc === 'object' ? fc.assessment : null;
  addKeyValue("FinSA Classification", finsaCategory);
  if (finsaBasis) addKeyValue("Basis", finsaBasis);
  // Add FinSA explanation paragraph
  y += 2;
  const finsaExplanation = finsaAssessment || (finsaCategory.toLowerCase().includes('professional') 
    ? "Client is classified as a Professional Client under FinSA Article 4. This classification is based on the entity's status as a regulated financial intermediary operating under FCA oversight through its contracted UK Appointed Representative arrangement. As a Professional Client, the entity is deemed to have the necessary knowledge and experience to understand the risks associated with the financial services and instruments offered by " + ((firmName || "").trim() || "the firm") + ". This classification has been confirmed by the MLRO and is consistent with the entity's business profile and operational sophistication."
    : 'FinSA classification confirmed by MLRO at onboarding.');
  setFont(9, "italic", colors.darkGrey);
  const finsaLines = doc.splitTextToSize(finsaExplanation, contentW);
  checkPage(finsaLines.length * 5 + 4);
  doc.text(finsaLines, ml, y);
  y += finsaLines.length * 5 + 4;
  // v1_849: the period the panel shows (the dashboard sets it), else the stamp or "set at approval".
  addKeyValue("Review Period", report.reviewPeriodShown ? report.reviewPeriod : reviewPeriodShown(report, { approved: !!(session.approved_at || session.mlro_decision === 'Approve') }));

  // ── 5. SOURCE OF FUNDS ──────────────────────────────────
  addSection("Source of Funds");
  addParagraph(narrative.sourceOfFunds);
  if (report.sofAssessment) {
    y += 2;
    addKeyValue("Funding Route", report.sofAssessment.fundingRoute);
    addKeyValue("Expected Volumes", report.sofAssessment.expectedVolumes || report.sofAssessment.monthlyVolume);
    // v1_747: reconciled assessment (officer acceptance first, model beneath).
    addKeyValue("Assessment", cs.sof.displayScore + (cs.sof.accepted && cs.sof.modelScore ? ` (the analysis assessed this ${String(cs.sof.modelScore).split(/[—–-]/)[0].trim()})` : ""));
  }

  // ── 6. SOURCE OF WEALTH ─────────────────────────────────
  if ((report.ubos || []).some(qualifies)) {
    addSection(`Source of Wealth (UBOs at or above ${ownT}%)`);
    addParagraph(narrative.sourceOfWealth);
    (report.ubos || []).filter(qualifies).forEach(ubo => {
      checkPage(20);
      y += 2;
      setFont(9, "bold", colors.darkGrey);
      doc.text(`${ubo.name} (${ubo.ownership}):`, ml, y);
      y += 5;

      // Pull from SOW session if available
      const sowData = sowSession?.report;
      const narrative_sow = sowData?.wealthOrigins || sowData?.sowNotes || ubo.sowNarrative;
      const assessment = cs.sow.accepted ? cs.sow.displayScore : (sowData?.sowAssessment || ubo.sowAssessment || "—");
      const evidence = sowData?.evidenceProvided || ubo.evidenceProvided;
      const mlroActions = sowData?.mlroActions || [];

      addKeyValue("Assessment", assessment, ml + 4);
      if (narrative_sow) addKeyValue("Wealth Narrative", narrative_sow, ml + 4);
      if (ubo.sowMlroRationale) addKeyValue("MLRO Rationale", ubo.sowMlroRationale, ml + 4);
      if (evidence) addKeyValue("Evidence Provided", evidence, ml + 4);
      if (sowData?.wealthTimeframe) addKeyValue("Timeframe", sowData.wealthTimeframe, ml + 4);
      if (sowData?.wealthStructure) addKeyValue("Held", sowData.wealthStructure, ml + 4);
      if (mlroActions.length > 0) {
        y += 2;
        setFont(8, "bold", colors.darkGrey);
        doc.text("MLRO Actions:", ml + 4, y); y += 4;
        mlroActions.forEach(action => {
          checkPage(6);
          setFont(8, "normal", colors.darkGrey);
          const lines = doc.splitTextToSize(`• ${action}`, contentW - 12);
          lines.forEach(line => { doc.text(line, ml + 8, y); y += 4; });
        });
      }
    });
  }

  // ── 7. AML SCREENING ────────────────────────────────────
  // ── TAX COMPLIANCE ──────────────────────────────────────
  addSection("Tax Compliance");
  {
    const tax = report.taxCompliance || {};
    const hasTax = tax.declared !== undefined || tax.declarationText || tax.notes || tax.riskIndicatorsPresent !== undefined;
    if (hasTax) {
      addKeyValue("Tax status declared", tax.declared === true ? "Yes" : tax.declared === false ? "No" : null);
      if (tax.declarationText) addParagraph(tax.declarationText, "Declaration");
      addKeyValue("Tax risk indicators", tax.riskIndicatorsPresent === true ? "Present (see below)" : tax.riskIndicatorsPresent === false ? "None identified" : null);
      if (tax.riskIndicatorsPresent && Array.isArray(tax.riskIndicators) && tax.riskIndicators.length) {
        setFont(9, "normal", colors.darkGrey);
        tax.riskIndicators.forEach(ri => { checkPage(5); doc.text(`• ${String(ri)}`, ml + 4, y); y += 5; });
      }
      if (tax.notes) addParagraph(tax.notes, "Notes");
    } else {
      addParagraph("No tax compliance information recorded.");
    }
  }

  addSection("AML Screening");
  addParagraph(narrative.amlScreening);
  // Entity sanctions & watchlist screening (OpenSanctions) + individual PEP/sanctions summary
  {
    // v1_747: read the reconciled (normalised) entity screening state, so the
    // block renders (the raw stored shape has no .status and was skipped) and its
    // verdict folds in the party results via sanctionsRowState.
    const entSanc = cs.screening.entity;
    if (entSanc && entSanc.status) {
      y += 2;
      setFont(9, "bold", colors.darkGrey);
      doc.text("Entity Sanctions & Watchlist Screening (OpenSanctions):", ml, y); y += 5;
      const clear = entSanc.status === "clear" || entSanc.status === "clear_with_notes";
      const resultText = entSanc.status === "clear" ? "No sanctions, PEP, or watchlist matches" : entSanc.status === "clear_with_notes" ? "Potential matches reviewed and cleared by MLRO" : `Status: ${entSanc.status}`;
      setFont(9, "normal", clear ? [34, 139, 34] : [200, 100, 0]);
      const el = doc.splitTextToSize(`${session.company_name || "Entity"}: ${resultText}`, contentW - 6);
      doc.text(el, ml + 4, y); y += el.length * 4.5 + 1;
      doc.setTextColor(...colors.black);
    }
    // Per-director/UBO OpenSanctions results (v1_645) — the analysis-time screen
    // of each individual, an independent OpenSanctions record shown alongside the
    // entity above. Distinct from the Sumsub identity summary that follows.
    const _osParties = partyScreeningRows(report, session.collected_data?.partyScreenings, session.collected_data?.screeningResolutions);
    if (_osParties.length) {
      y += 2;
      setFont(9, "bold", colors.darkGrey);
      doc.text("Individual Parties (OpenSanctions Screening):", ml, y); y += 5;
      for (const pr of _osParties) {
        const clear = pr.status === "clear";
        setFont(9, "normal", clear ? [34, 139, 34] : pr.status === "not_screened" ? colors.darkGrey : [200, 100, 0]);
        const when = pr.checkedAt ? new Date(pr.checkedAt).toLocaleDateString("en-GB") : "";
        const pl = doc.splitTextToSize(`${pr.name} (${pr.role}): ${PARTY_STATUS_LABEL[pr.status] || pr.status}${when ? " · " + when : ""} · ${pr.source}`, contentW - 6);
        checkPage(pl.length * 4.5 + 2);
        doc.text(pl, ml + 4, y); y += pl.length * 4.5 + 1;
      }
      doc.setTextColor(...colors.black);
    }
    if (cs.partyCount > 0) {
      // v1_747: counted over the reconciled verification party set (deduped), not the raw rows.
      const screened = cs.partyCount;
      const cleared = cs.verifiedCount;
      y += 2;
      setFont(9, "bold", colors.darkGrey);
      doc.text("Individual PEP & Sanctions Screening (Sumsub):", ml, y); y += 5;
      setFont(9, "normal", colors.black);
      const il = doc.splitTextToSize(`${screened} named individual(s) screened for identity, PEP, and sanctions exposure; ${cleared} cleared. Per-person results appear in the Beneficial Ownership & Identity Verification section.`, contentW - 6);
      doc.text(il, ml + 4, y); y += il.length * 4.5 + 1;
    }
  }
  // Form A in the exports (22/10/2026): an individual client's Form A status,
  // in the Audit Record's words; said as not read when the rows were not passed.
  if (isIndividualCase(session.collected_data)) {
    const fa = individualFormALine({ report, collected: session.collected_data, formRequests: formA && formA.formRequests, formAState: formA && formA.individualFormA });
    y += 2;
    checkPage(14);
    setFont(9, "bold", colors.darkGrey);
    doc.text("Form A (the client):", ml, y); y += 5;
    setFont(9, "normal", colors.black);
    const fl = doc.splitTextToSize(fa, contentW - 6);
    doc.text(fl, ml + 4, y); y += fl.length * 4.5 + 1;
  }
  if (walletScreenings.length > 0) {
    y += 2;
    setFont(9, "bold", colors.darkGrey);
    doc.text("Blockchain Analytics (Chainalysis):", ml, y); y += 5;
    walletScreenings.forEach(w => {
      checkPage(10);
      const addr = w.address || w.wallet_address || "";
      const risk = w.risk_level || w.risk_rating || "—";
      const score = w.risk_score !== undefined ? ` · Score: ${w.risk_score}/10` : "";
      const asset = w.asset || "";
      const riskColor = risk.toLowerCase() === "low" ? [34, 139, 34] : risk.toLowerCase() === "medium" ? [200, 100, 0] : risk.toLowerCase() === "high" ? [180, 0, 0] : colors.darkGrey;
      setFont(8, "bold", colors.darkGrey);
      doc.text(`${asset} — ${addr.slice(0, 20)}...${addr.slice(-8)}`, ml + 4, y); y += 4.5;
      setFont(8, "normal", colors.darkGrey);
      doc.setTextColor(...riskColor);
      doc.text(`Risk: ${risk}${score} · ✓ Screened`, ml + 8, y);
      doc.setTextColor(...colors.darkGrey);
      if (w.cluster_name) { y += 4; doc.text(`Cluster: ${w.cluster_name}`, ml + 8, y); }
      y += 5;
    });
  } else if ((report.walletAddresses || session.collected_data?.walletAddresses || []).length > 0) {
    y += 2;
    setFont(9, "normal", colors.darkGrey);
    doc.text("Wallet addresses provided — blockchain analytics screening pending.", ml, y); y += 5;
  }

  // ── 8. DOCUMENTS ON FILE ────────────────────────────────
  // ── CLIENT AML/CFT CONTROLS ─────────────────────────────
  addSection("Client AML/CFT Controls");
  {
    const aml = report.amlPolicyAssessment || {};
    const yn = b => b === true ? "Yes" : b === false ? "No" : null;
    const hasAml = aml.provided !== undefined || aml.score || aml.hasKYCProcedure !== undefined || aml.hasMLRO !== undefined;
    if (hasAml) {
      addKeyValue("AML/CFT policy provided", yn(aml.provided));
      addKeyValue("Assessment", aml.score);
      addKeyValue("KYC / CDD procedure", yn(aml.hasKYCProcedure));
      addKeyValue("Appointed MLRO", yn(aml.hasMLRO));
      addKeyValue("Sanctions screening", yn(aml.hasSanctionsScreening));
      addKeyValue("Transaction monitoring", yn(aml.hasTransactionMonitoring));
    } else {
      addParagraph("No AML/CFT control framework information recorded for this client.");
    }
  }

  // ── WEBSITE & ONLINE VERIFICATION ───────────────────────
  addSection("Website & Online Verification");
  {
    const web = report.websiteFindings || {};
    if (web.url || web.summary) {
      addKeyValue("Website", web.url);
      if (web.summary) addParagraph(web.summary, "Findings");
      const bullets = (label, arr) => {
        if (Array.isArray(arr) && arr.length) {
          checkPage(6); setFont(9, "bold", [200, 100, 0]); doc.text(label, ml, y); y += 5;
          setFont(9, "normal", colors.darkGrey);
          arr.forEach(f => { checkPage(5); doc.text(`• ${String(f)}`, ml + 4, y); y += 5; });
        }
      };
      bullets("Red flags:", web.redFlags);
      bullets("Contradictions:", web.contradictions);
    } else {
      addParagraph("No website or online verification findings recorded.");
    }
  }

  addSection("Documents on File");
  checkPage(10);
  // Table header
  doc.setFillColor(...colors.accent);
  doc.rect(ml, y, contentW, 7, "F");
  setFont(8, "bold", colors.white);
  doc.text("Document", ml + 2, y + 4.5);
  doc.text("Type", ml + 78, y + 4.5);
  doc.text("Status", ml + 118, y + 4.5);
  doc.text("View", ml + 150, y + 4.5);
  y += 7;

  const allDocs = (documents && documents.length > 0)
    ? documents.map(d => ({ name: d.file_name || d.document_name || "—", type: d.doc_type || d.document_type || "—", status: d.assessment || "Received", url: d.viewUrl || null })) // v1_873: only a dashboard link by id, never a stored file URL
    : (narrative.documentsOnFile || []).map(d => ({ name: d.name, type: d.type || "—", status: d.status || "Received", url: null }));

  allDocs.forEach((d, i) => {
    checkPage(7);
    if (i % 2 === 0) {
      doc.setFillColor(...colors.veryLight);
      doc.rect(ml, y, contentW, 6, "F");
    }
    setFont(8, "normal", colors.black);
    const nameLines = doc.splitTextToSize(d.name || "—", 70);
    doc.text(nameLines[0], ml + 2, y + 4);
    doc.text(String(d.type || "—").slice(0, 20), ml + 78, y + 4);
    const statusColor = (d.status || "").toLowerCase().includes("satisf") || (d.status || "").toLowerCase().includes("receiv") ? [34, 139, 34] : colors.darkGrey;
    doc.setTextColor(...statusColor);
    doc.text(String(d.status || "Received").slice(0, 14), ml + 118, y + 4);
    doc.setTextColor(...colors.black);
    if (d.url) {
      setFont(8, "bold", [21, 101, 192]);
      doc.text("View", ml + 150, y + 4);
      const lw = doc.getTextWidth("View");
      doc.link(ml + 150, y + 0.8, lw, 4.4, { url: d.url });
      doc.setTextColor(...colors.black);
    }
    y += 6;
  });
  y += 3;

  // ── 9. RISK FACTORS & MITIGANTS ─────────────────────────
  addSection("Risk Factors & Mitigants");
  addParagraph(narrative.riskFactors);

  // ── 10. ROSS'S RECOMMENDATION ───────────────────────────
  addSection("Compliance Manager Recommendation");
  addParagraph(narrative.recommendation);
  if (narrative.conditions) {
    y += 2;
    addParagraph(`Conditions: ${narrative.conditions}`, "Conditions attached to approval");
  }

  // ── 11. CASE NOTES SUMMARY ──────────────────────────────
  // ── MLRO DECISION & SIGN-OFF ────────────────────────────
  addSection("MLRO Decision & Sign-Off");
  {
    const dec = (session.mlro_decision || "").toString();
    const decLabel = dec ? (/approv/i.test(dec) ? "APPROVED" : /(declin|reject)/i.test(dec) ? "DECLINED" : dec) : "Pending";
    addKeyValue("Decision", decLabel);
    if (session.approved_at) addKeyValue("Decision date", new Date(session.approved_at).toLocaleDateString("en-GB"));
    if (session.next_review_date) addKeyValue("Next periodic review", new Date(session.next_review_date).toLocaleDateString("en-GB"));
    if (session.mlro_notes) addParagraph(session.mlro_notes, "MLRO rationale & notes");
    if (!session.mlro_notes && !session.approved_at) addParagraph("Awaiting MLRO decision and sign-off.");
  }

  addSection("Key Compliance Actions");
  checkPage(10);
  setFont(9, "normal", colors.darkGrey);
  const keyNotes = [];
  if (session.approved_at) keyNotes.push(`Client approved: ${new Date(session.approved_at).toLocaleDateString("en-GB")} by ${session.mlro_decision}`);
  if (session.next_review_date) keyNotes.push(`Next periodic review due: ${new Date(session.next_review_date).toLocaleDateString("en-GB")}`);
  if (session.last_review_date) keyNotes.push(`Last review completed: ${new Date(session.last_review_date).toLocaleDateString("en-GB")} by ${session.last_review_by}`);
  keyNotes.forEach(note => { checkPage(5); doc.text(`• ${note}`, ml, y); y += 5; });
  y += 4;

  // ── FOOTER on all pages ─────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFillColor(...colors.veryLight);
    doc.rect(0, H - 12, W, 12, "F");
    doc.setDrawColor(...colors.lightGrey);
    doc.line(0, H - 12, W, H - 12);
    setFont(7, "normal", colors.midGrey);
    doc.text(`CONFIDENTIAL — Client Due Diligence Report · ${firmBrand}`, ml, H - 5);
    doc.text(`Page ${p} of ${totalPages} · Ref: ${ref}`, W - mr - doc.getTextWidth(`Page ${p} of ${totalPages} · Ref: ${ref}`), H - 5);
  }

  const filename = `${firmFilePrefix}_${(session.company_name || "Client").replace(/\s+/g, "_")}_Acceptance_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
  // Return a viewable blob URL + filename. Programmatic opening/downloading is unreliable on
  // iOS Safari (doc.save's download attribute is ignored; window.open after an await is blocked),
  // so the caller presents a tappable link instead — the open then happens on a real user gesture.
  const blobUrl = doc.output("bloburl");
  return { blobUrl, filename };
}
